import React from "react"

import { differenceInDays, parseISO, startOfDay } from "date-fns"
import { ChevronDown, ChevronRight, Package, Truck } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { CollapsibleTrigger } from "@/components/ui/collapsible"
import { useAuth } from "@/contexts/AuthContext"
import { formatDayLong } from "@/lib/dateFormats"
import { formatCost } from "@/lib/routeCostCalculation"
import type { PlannedRoute } from "@/types/collections"

import type { RouteMetrics } from "./useRouteMetrics"

/** Plain mean of each route's fill %, not used over total capacity. */
function averageLoadFactorLabel(capacities: RouteMetrics["capacityInfo"][]): string {
  let totalFillPercentage = 0
  let routesWithCapacity = 0
  capacities.forEach((capacityInfo) => {
    if (capacityInfo) {
      totalFillPercentage += capacityInfo.totalFillPercentage
      routesWithCapacity += 1
    }
  })

  if (routesWithCapacity === 0) return "N/A"
  const avgFillPercentage = totalFillPercentage / routesWithCapacity
  if (isNaN(avgFillPercentage) || !isFinite(avgFillPercentage)) return "N/A"
  return `${avgFillPercentage.toFixed(0)}% avg load factor`
}

interface PlanningDateGroupHeaderProps {
  dateKey: string
  dateRoutes: PlannedRoute[]
  isExpanded: boolean
  metricsFor: (route: PlannedRoute) => RouteMetrics
}

const PlanningDateGroupHeader: React.FC<PlanningDateGroupHeaderProps> = ({
  dateKey,
  dateRoutes,
  isExpanded,
  metricsFor,
}) => {
  const { can } = useAuth()
  const dateObj = parseISO(dateKey)
  const daysFromNow = differenceInDays(dateObj, startOfDay(new Date()))
  const metrics = dateRoutes.map(metricsFor)

  const routesAtOrOverLoadFactor = metrics.filter(
    (m) => m.capacityInfo && m.capacityInfo.totalFillPercentage >= 99,
  ).length

  return (
    <CollapsibleTrigger asChild>
      <div className="flex items-center justify-between px-4 py-3 bg-palette-jungleGreen000 border-b border-palette-jungleGreen200 cursor-pointer">
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown size={16} className="text-palette-jungleGreen400" />
          ) : (
            <ChevronRight size={16} className="text-palette-jungleGreen400" />
          )}
          <div>
            <span className="font-semibold text-palette-jungleGreen900">
              {formatDayLong(dateObj)}
            </span>
            <span className="ml-2 text-xs text-palette-jungleGreen500">
              {daysFromNow === 0
                ? "Today"
                : daysFromNow === 1
                  ? "Tomorrow"
                  : `In ${daysFromNow} days`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100">
            {dateRoutes.length} route{dateRoutes.length !== 1 ? "s" : ""}
          </Badge>
          {routesAtOrOverLoadFactor > 0 ? (
            <Badge
              variant="secondary"
              className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100"
            >
              {routesAtOrOverLoadFactor} LF issues
            </Badge>
          ) : null}
          <span className="text-sm text-palette-jungleGreen500">
            {dateRoutes.reduce((acc, r) => acc + r.totalStops, 0)} stops
          </span>
          <span className="text-sm text-palette-jungleGreen500 flex items-center gap-1">
            <Package size={12} />
            {metrics.reduce((acc, m) => acc + m.volume, 0).toLocaleString()} items
          </span>
          {can.costs && (
            <>
              <span className="text-sm text-palette-jungleGreen500">
                Est. {formatCost(metrics.reduce((acc, m) => acc + (m.cost?.total_cost || 0), 0))}
              </span>
              <span className="text-sm text-palette-jungleGreen500 flex items-center gap-1">
                <Truck size={12} />
                {averageLoadFactorLabel(metrics.map((m) => m.capacityInfo))}
              </span>
            </>
          )}
        </div>
      </div>
    </CollapsibleTrigger>
  )
}

export default PlanningDateGroupHeader
